import { useState } from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';

import { useAppSelector } from '../../../../../../../app/hooks';
import { getRiskType, RISK_MAP, RISK_TYPES } from '../aicaGradeConstants';
import { KEYBOARD_EVENTS } from '../../../../../../../utils/constants/commonConstants';

import MetricRiskBadge from '../MetricRiskBadge/MetricRiskBadge';
import SideDrawer from '../../../../../../../common/_custom/SideDrawer/SideDrawer';
import Tooltip from '../../../../../../../common/_custom/Tooltip/Tooltip';
import { useIsMobile } from '../../../../../../../common/_custom/Hooks/useIsMobile';

import infoIcon from '../../../../../../../assets/info.svg';

import styles from './AicaGradeSideDrawer.module.scss';

function AicaGradeSideDrawer({ showDrawer = false, closeDrawer = () => {}, isPDFReport = false }) {
    const aicaGradeData = useAppSelector((state) => state.dashboard.dashboardReducer.aicaGradeData);
    // Portal the section-header tooltips so they don't widen the page / run off-screen on mobile.
    const isMobile = useIsMobile();
    // Mobile: tap a submetric card to expand/collapse its description (one open at a time).
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    const render = (
        <div
            className={clsx({
                [styles.AicaGradeSideDrawer]: !isPDFReport,
                [styles.SideDrawerPDFReport]: isPDFReport,
            })}
        >
            <h1 className={styles.Title}>Risk Parameters</h1>

            <div className={styles.Content}>
                <div className={styles.LegendContainer}>
                    {Object.entries(RISK_MAP).map(([gradeKey, gradeData]) => (
                        <div className={styles.SingleLegend} key={gradeKey}>
                            <div
                                className={styles.Icon}
                                style={{
                                    backgroundColor: gradeKey === RISK_TYPES.SEVERE ? gradeData.background : gradeData.color,
                                }}
                            ></div>
                            {gradeData.label}
                        </div>
                    ))}
                </div>

                <div className={styles.MetricsData}>
                    {aicaGradeData?.headers.map((data, i) => (
                        <div
                            className={clsx(styles.MainMetric, {
                                [styles.NoData]: !data?.metrics?.length,
                            })}
                            key={`${data.displayName}-${i}`}
                        >
                            <div className={styles.Top}>
                                <div className={styles.Left}>
                                    <span className={styles.MetricNameText}>{data.displayName}</span>

                                    {!!data?.tooltipDescription && !isPDFReport && (
                                        <Tooltip
                                            content={data.tooltipDescription}
                                            direction="right"
                                            usePortal={true}
                                            triggerOnClick={isMobile}
                                            tipClassName={styles.TooltipWidth}
                                        >
                                            <img src={infoIcon} alt="score" height="18px" />
                                        </Tooltip>
                                    )}
                                    <MetricRiskBadge score={data?.headerScore} isPDFReport={isPDFReport} />
                                </div>
                            </div>
                            {!!data?.metrics?.length && (
                                <div className={styles.Submetrics}>
                                    {data?.metrics?.map((metric, j) => {
                                        const riskType = RISK_MAP[getRiskType(metric?.metricScore)];
                                        const isRiskTypeSevere = riskType?.label === 'Severe';
                                        const isNoData = riskType?.label === 'No Data' || isEmpty(metric?.tooltipDescription);
                                        const cardKey = `${i}-${j}`;
                                        return (
                                            <div
                                                className={clsx(styles.SubmetricCard, {
                                                    [styles.NoData]: isNoData,
                                                    [styles.Expanded]: isMobile && expandedKey === cardKey,
                                                })}
                                                key={cardKey}
                                                onClick={
                                                    isMobile && !isNoData
                                                        ? () => setExpandedKey((prev) => (prev === cardKey ? null : cardKey))
                                                        : undefined
                                                }
                                                onKeyDown={
                                                    isMobile && !isNoData
                                                        ? (e) => {
                                                              if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                                                                  e.preventDefault();
                                                                  setExpandedKey((prev) => (prev === cardKey ? null : cardKey));
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                role={isMobile && !isNoData ? 'button' : undefined}
                                                tabIndex={isMobile && !isNoData ? 0 : undefined}
                                                aria-expanded={isMobile && !isNoData ? expandedKey === cardKey : undefined}
                                                style={{
                                                    backgroundColor: isRiskTypeSevere ? '#E58588' : riskType?.background,
                                                    color: isRiskTypeSevere ? 'white' : 'black',
                                                }}
                                            >
                                                <div className={styles.SubmetricCardHeading}>
                                                    <div
                                                        className={styles.Icon}
                                                        style={{
                                                            backgroundColor: isRiskTypeSevere ? riskType?.background : riskType?.color,
                                                        }}
                                                    ></div>
                                                    <div className={styles.Text}>{metric?.displayName}</div>
                                                </div>
                                                <div className={styles.Description}>
                                                    <div className={styles.DescriptionText}>{metric?.tooltipDescription}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (isPDFReport) return render;
    else
        return (
            <>
                {showDrawer && (
                    <SideDrawer allowClose heading="Risk Parameters" onClose={closeDrawer} width="70%">
                        {render}
                    </SideDrawer>
                )}
            </>
        );
}

export default AicaGradeSideDrawer;
